import { useEffect, useState } from 'react';

import membershipsApi from '@/src/api/membershipsApi';
import { useSessionUser } from '@/src/lib/sessionStore';

/**
 * The purchase this user has in flight, for the surfaces that only need to say
 * so — home's banner today.
 *
 * ── Why it fetches at all ───────────────────────────────────────────────
 * `/me` reports a pending membership as a SLIM block: `{id, status,
 * checkoutPayload}`, no plan and no dates (see [[PendingMembership]]). So the
 * session alone can say THAT a payment is outstanding but not what it's for, and
 * naming the pass is the whole point of the banner. `GET /me/memberships/{id}`
 * is where the name lives.
 *
 * ── The name is optional, the pending state isn't ───────────────────────
 * `planName` comes back null while the fetch is in the air and stays null if it
 * fails. That's deliberate: the fact worth surfacing is the outstanding payment,
 * which the session already knows for certain, so a slow or dead detail call
 * must soften the copy rather than hide the banner. Hence no loading or error in
 * the state — there is nothing for a caller to draw for either, and nothing to
 * retry that matters.
 */

export type PendingMembershipState = {
  /** The membership to open — `/my-memberships/{id}`. */
  id: string;
  /** The plan's name, or null while unknown. See the docstring. */
  planName: string | null;
};

export function usePendingMembership(): PendingMembershipState | null {
  const id = useSessionUser()?.pendingMembership?.id ?? null;
  // The id is kept WITH the name so a name fetched for a previous purchase can't
  // be read against the current one. Resetting it on change would mean a
  // synchronous setState in the effect below; this needs neither.
  const [named, setNamed] = useState<PendingMembershipState | null>(null);

  useEffect(() => {
    if (id === null) return;

    let isCurrent = true;
    membershipsApi
      .getMyMembership(id)
      .then((membership) => {
        if (isCurrent) setNamed({ id, planName: membership.plan.name });
      })
      // Swallowed on purpose — the banner falls back to unnamed copy, and there
      // is no screen state a failure here should change.
      .catch(() => {});

    return () => {
      isCurrent = false;
    };
  }, [id]);

  if (id === null) return null;
  return { id, planName: named?.id === id ? named.planName : null };
}
